import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useUISettings, BACKGROUND_PRESETS, UI_DENSITIES } from '../context/UISettingsContext';
import apiClient from '../api/client';
import { cancelSubscription, reactivateSubscription, updatePaymentMethod } from '../api/stripe';
import { getMyClasses, joinClass, createClass, leaveClass, updateClassVisibility } from '../api/studyClasses';
import { startStripeCheckout } from '../utils/upgradeFlow';
import UsageDashboard from './UsageDashboard';
import Toast from './Toast';
import AIDisclaimer from './AIDisclaimer';
import '../pages/Settings.css';
import './SettingsModal.css';

const PRESET_LABELS = {
  void: 'Void',
  carbon: 'Carbon',
  neutral: 'Neutral',
  warm: 'Warm',
  soft: 'Soft',
};
const PRESET_SWATCH = {
  void: '#0a0a0a',
  carbon: '#1d1d20',
  neutral: '#262626',
  warm: '#383838',
  soft: '#4d4d4d',
};

const DENSITY_LABELS = {
  compact: 'Compact',
  standard: 'Standard',
  spacious: 'Spacious',
};

function AppearanceSection() {
  const {
    backgroundPreset,
    setBackgroundPreset,
    uiDensity,
    setUIDensity,
  } = useUISettings();
  return (
    <section className="settings-section">
      <div className="section-label">
        <span className="section-label-text">Appearance</span>
        <div className="section-label-line" />
      </div>
      <div className="settings-card">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>Background brightness</span>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {PRESET_LABELS[backgroundPreset] || backgroundPreset}
            </span>
          </div>
          <p className="plan-upgrade-hint" style={{ margin: 0 }}>
            Shifts the base canvas from pure black toward lighter grays. Text and accents stay fixed.
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            {BACKGROUND_PRESETS.map((preset) => {
              const active = preset === backgroundPreset;
              return (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setBackgroundPreset(preset)}
                  aria-pressed={active}
                  style={{
                    flex: 1,
                    height: 52,
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                    borderRadius: 8,
                    border: active
                      ? '2px solid var(--accent-primary)'
                      : '1px solid var(--border-default)',
                    background: PRESET_SWATCH[preset],
                    color: active ? '#fff' : 'var(--text-secondary)',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    transition: 'border 120ms ease, color 120ms ease',
                  }}
                >
                  {PRESET_LABELS[preset]}
                </button>
              );
            })}
          </div>
          <div className="sm-setting-divider" />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>UI density</span>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {DENSITY_LABELS[uiDensity] || uiDensity}
            </span>
          </div>
          <p className="plan-upgrade-hint" style={{ margin: 0 }}>
            Scales sidebar width and carousel tray height in lockstep.
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            {UI_DENSITIES.map((density) => {
              const active = density === uiDensity;
              return (
                <button
                  key={density}
                  type="button"
                  onClick={() => setUIDensity(density)}
                  aria-pressed={active}
                  style={{
                    flex: 1,
                    height: 52,
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                    borderRadius: 8,
                    border: active
                      ? '2px solid var(--accent-primary)'
                      : '1px solid var(--border-default)',
                    background: 'rgba(255, 255, 255, 0.02)',
                    color: active ? '#fff' : 'var(--text-secondary)',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    transition: 'border 120ms ease, color 120ms ease, background 120ms ease',
                  }}
                >
                  {DENSITY_LABELS[density]}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function TutorialTestCard() {
  const [launching, setLaunching] = useState(false);
  const [result, setResult] = useState(null);

  const launchTest = async () => {
    setLaunching(true);
    setResult(null);
    try {
      const resp = await apiClient.post('/api/auth/tutorial-test');
      const { access_token, user_id } = resp.data;
      const url = `/tutorial-test#token=${encodeURIComponent(access_token)}&user_id=${encodeURIComponent(user_id)}`;
      window.open(url, '_blank');
      setResult('Opened in new tab');
    } catch (err) {
      setResult(err.response?.data?.detail || 'Failed');
    } finally {
      setLaunching(false);
    }
  };

  return (
    <section className="settings-section">
      <div className="settings-card" style={{ marginTop: 0 }}>
        <div className="plan-overview">
          <div className="plan-header-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>Tutorial</span>
              <p className="plan-upgrade-hint" style={{ margin: '4px 0 0' }}>
                Opens the guided walkthrough in a new tab with a fresh demo account.
              </p>
              {result && (
                <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#a5b4fc' }}>{result}</p>
              )}
            </div>
            <button
              onClick={launchTest}
              disabled={launching}
              style={{
                padding: '6px 16px',
                borderRadius: 8,
                border: 'none',
                background: 'rgba(99, 102, 241, 0.2)',
                color: '#a5b4fc',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: launching ? 'not-allowed' : 'pointer',
                flexShrink: 0,
              }}
            >
              {launching ? 'Creating...' : 'Launch Tutorial'}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function SettingsModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { user, checkAuth, logout } = useAuth();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [unlockAllGolden, setUnlockAllGolden] = useState(false);
  const [goldenToggleLoading, setGoldenToggleLoading] = useState(false);

  // Study Class state
  const [myClasses, setMyClasses] = useState([]);
  const [classLoading, setClassLoading] = useState(true);
  const [joinCodeInput, setJoinCodeInput] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [leaveConfirm, setLeaveConfirm] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [naplexResetLoading, setNaplexResetLoading] = useState(false);
  const [naplexResetResult, setNaplexResetResult] = useState(null);

  // Load admin settings when modal opens
  useEffect(() => {
    if (isOpen && user?.role === 'admin') {
      apiClient.get('/api/auth/settings')
        .then(res => {
          setUnlockAllGolden(res.data.unlock_all_golden || false);
        })
        .catch(() => {});
    }
  }, [isOpen, user?.role]);

  // Load study classes when modal opens
  useEffect(() => {
    if (!isOpen) return;
    setClassLoading(true);
    getMyClasses()
      .then(data => setMyClasses(data))
      .catch((err) => {
        console.warn('Failed to load study classes:', err?.message || err);
        setToastMessage('Failed to load study classes');
        setShowToast(true);
      })
      .finally(() => setClassLoading(false));
  }, [isOpen]);

  // Handle return from Stripe payment portal
  useEffect(() => {
    if (isOpen) {
      const params = new URLSearchParams(window.location.search);
      if (params.get('payment_updated') === 'success') {
        setToastMessage('Payment method updated successfully');
        setShowToast(true);
        const url = new URL(window.location);
        url.searchParams.delete('payment_updated');
        window.history.replaceState({}, '', url);
        checkAuth();
      }
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle downgrade notification
  useEffect(() => {
    if (isOpen && user?.last_downgrade_at) {
      const downgradedAt = new Date(user.last_downgrade_at * 1000).getTime();
      const notifiedAt = sessionStorage.getItem('downgrade_notified_at');
      if (!notifiedAt || parseInt(notifiedAt) < downgradedAt) {
        setToastMessage("Your subscription has ended. You're now on the Free plan.");
        setShowToast(true);
        sessionStorage.setItem('downgrade_notified_at', downgradedAt.toString());
      }
    }
  }, [isOpen, user?.last_downgrade_at]);

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const handleJoinClass = async () => {
    if (!joinCodeInput.trim()) return;
    setJoinLoading(true);
    try {
      await joinClass(joinCodeInput.trim());
      setJoinCodeInput('');
      const data = await getMyClasses();
      setMyClasses(data);
      setToastMessage('Joined class!');
      setShowToast(true);
    } catch (error) {
      setToastMessage(error.response?.data?.detail || 'Invalid join code');
      setShowToast(true);
    } finally {
      setJoinLoading(false);
    }
  };

  const handleCreateClass = async () => {
    if (!createName.trim()) return;
    setCreateLoading(true);
    try {
      await createClass(createName.trim());
      setCreateName('');
      setShowCreateForm(false);
      const data = await getMyClasses();
      setMyClasses(data);
      setToastMessage('Class created!');
      setShowToast(true);
    } catch (error) {
      setToastMessage(error.response?.data?.detail || 'Failed to create class');
      setShowToast(true);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleLeaveClass = async (classId) => {
    try {
      await leaveClass(classId);
      setMyClasses(prev => prev.filter(c => c.id !== classId));
      setLeaveConfirm(null);
      setToastMessage('Left class');
      setShowToast(true);
    } catch (error) {
      setToastMessage(error.response?.data?.detail || 'Failed to leave class');
      setShowToast(true);
    }
  };

  const handleToggleVisibility = async (classId, currentShowName) => {
    try {
      await updateClassVisibility(classId, !currentShowName);
      setMyClasses(prev => prev.map(c =>
        c.id === classId ? { ...c, _showName: !currentShowName } : c
      ));
      setToastMessage(!currentShowName ? 'Name visible to classmates' : 'Name hidden from classmates');
      setShowToast(true);
    } catch (error) {
      setToastMessage(error.response?.data?.detail || 'Failed to update visibility');
      setShowToast(true);
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    });
  };

  const handleCancelSubscription = async () => {
    setIsLoading(true);
    try {
      await cancelSubscription();
      setToastMessage("Subscription cancelled. You'll keep premium access until your billing period ends.");
      setShowToast(true);
      setShowCancelModal(false);
      await checkAuth();
    } catch (error) {
      console.error('Cancel error:', error);
      setToastMessage(error.response?.data?.detail || 'Failed to cancel subscription');
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setIsLoading(true);
    try {
      await reactivateSubscription();
      setToastMessage('Subscription reactivated! Your premium access will continue.');
      setShowToast(true);
      await checkAuth();
    } catch (error) {
      console.error('Reactivate error:', error);
      setToastMessage(error.response?.data?.detail || 'Failed to reactivate subscription');
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    setIsLoading(true);
    try {
      const { portal_url } = await updatePaymentMethod();
      window.location.href = portal_url;
    } catch (error) {
      console.error('Payment portal error:', error);
      setToastMessage(error.response?.data?.detail || 'Failed to open payment portal');
      setShowToast(true);
      setIsLoading(false);
    }
  };

  const handleApplyCode = async () => {
    if (!codeInput.trim()) return;
    setCodeLoading(true);
    try {
      const res = await apiClient.post('/api/auth/apply-referral', {
        referral_code: codeInput.trim(),
      });
      setToastMessage(res.data.detail || 'Code applied!');
      setShowToast(true);
      setCodeInput('');
      await checkAuth();
    } catch (error) {
      setToastMessage(error.response?.data?.detail || 'Invalid code');
      setShowToast(true);
    } finally {
      setCodeLoading(false);
    }
  };

  const handleToggleUnlockAllGolden = async () => {
    setGoldenToggleLoading(true);
    const newValue = !unlockAllGolden;
    try {
      await apiClient.patch('/api/auth/settings', { unlock_all_golden: newValue });
      setUnlockAllGolden(newValue);
      setToastMessage(newValue ? 'Unlock All Golden enabled' : 'Unlock All Golden disabled');
      setShowToast(true);
    } catch (error) {
      setToastMessage(error.response?.data?.detail || 'Failed to update setting');
      setShowToast(true);
    } finally {
      setGoldenToggleLoading(false);
    }
  };

  if (!isOpen) return null;

  const isPremium = user?.subscription_tier === 'premium';
  const isCancelled = user?.cancel_at_period_end === true;
  const hasNaplexDlc = !!user?.naplex_dlc;
  const hasPtcbDlc = !!user?.ptcb_dlc;
  const hasMpjeDlc = !!user?.mpje_dlc;
  const isNaplexDlc = hasNaplexDlc && !user?.stripe_subscription_id;
  const ownedAddOns = [
    hasNaplexDlc ? 'NAPLEX DLC' : null,
    hasPtcbDlc ? 'PTCB DLC' : null,
    hasMpjeDlc ? 'MPJE DLC' : null,
  ].filter(Boolean);
  const renewalDate = user?.subscription_current_period_end
    ? new Date(user.subscription_current_period_end * 1000).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : null;
  const openPricingModal = (tier = null) => {
    // Default to annual (best deal, includes NAPLEX) if no specific tier.
    const targetTier = tier || 'annual';
    onClose();
    startStripeCheckout(targetTier, { fallbackNavigate: navigate });
  };

  return (
    <>
      <div className="sm-overlay" onClick={() => !isLoading && onClose()}>
        <div className="sm-modal" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="sm-header">
            <span className="sm-title">Settings</span>
            <button className="sm-close" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Scrollable body */}
          <div className="sm-body">
            <AppearanceSection />
            {/* Subscription Section */}
            <section className="settings-section">
              <div className="section-label">
                <span className="section-label-text">Subscription</span>
                <div className="section-label-line" />
              </div>

              <div className="settings-card">
                {isPremium ? (
                  <>
                    {isNaplexDlc ? (
                      <div className="plan-overview">
                        <div className="plan-header-row">
                          <div className="plan-name-group">
                            <span className="plan-tier-badge plan-tier-premium" style={{ background: 'rgba(168, 85, 247, 0.2)', borderColor: 'rgba(168, 85, 247, 0.4)', color: '#c084fc' }}>NAPLEX DLC</span>
                            <span className="plan-status-dot plan-status-active" />
                            <span className="plan-status-text">Active</span>
                          </div>
                        </div>
                        <div className="plan-meta">
                          <span className="plan-meta-label">Type</span>
                          <span className="plan-meta-value">Lifetime access</span>
                        </div>
                        {ownedAddOns.length > 1 && (
                          <div className="plan-meta">
                            <span className="plan-meta-label">Add-ons</span>
                            <span className="plan-meta-value">{ownedAddOns.join(', ')}</span>
                          </div>
                        )}
                      </div>
                    ) : isCancelled ? (
                      <div className="cancellation-banner">
                        <div className="cancellation-banner-icon">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="16" x2="12" y2="12" />
                            <line x1="12" y1="8" x2="12.01" y2="8" />
                          </svg>
                        </div>
                        <div className="cancellation-banner-content">
                          <p>
                            Your Premium subscription ends on{' '}
                            <strong>{renewalDate}</strong>.
                            You can reactivate anytime before then.
                          </p>
                          <button
                            onClick={handleReactivateSubscription}
                            disabled={isLoading}
                            className="settings-btn settings-btn-primary"
                          >
                            {isLoading ? 'Reactivating...' : 'Reactivate Subscription'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="plan-overview">
                        <div className="plan-header-row">
                          <div className="plan-name-group">
                            <span className="plan-tier-badge plan-tier-premium">Premium</span>
                            <span className="plan-status-dot plan-status-active" />
                            <span className="plan-status-text">Active</span>
                          </div>
                        </div>
                        <div className="plan-meta">
                          <span className="plan-meta-label">Next billing</span>
                          <span className="plan-meta-value">{renewalDate || '--'}</span>
                        </div>
                        <div className="plan-meta">
                          <span className="plan-meta-label">Billing cycle</span>
                          <span className="plan-meta-value">
                            {user?.billing_interval === 'year' ? 'Annual' : 'Monthly'}
                          </span>
                        </div>
                        <div className="plan-meta">
                          <span className="plan-meta-label">Amount</span>
                          <span className="plan-meta-value">
                            {user?.billing_interval === 'year' ? '$120/year' : '$15/month'}
                          </span>
                        </div>
                        {ownedAddOns.length > 0 && (
                          <div className="plan-meta">
                            <span className="plan-meta-label">Add-ons</span>
                            <span className="plan-meta-value">{ownedAddOns.join(', ')}</span>
                          </div>
                        )}
                      </div>
                    )}
                    {!isNaplexDlc && (
                    <div className="card-actions">
                      {!isCancelled ? (
                        <>
                          <button
                            onClick={handleUpdatePaymentMethod}
                            disabled={isLoading}
                            className="settings-btn settings-btn-secondary"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                              <line x1="1" y1="10" x2="23" y2="10" />
                            </svg>
                            {isLoading ? 'Loading...' : 'Update Payment'}
                          </button>
                          <button
                            onClick={() => setShowCancelModal(true)}
                            disabled={isLoading}
                            className="settings-btn settings-btn-danger"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={handleUpdatePaymentMethod}
                          disabled={isLoading}
                          className="settings-btn settings-btn-secondary"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                            <line x1="1" y1="10" x2="23" y2="10" />
                          </svg>
                          {isLoading ? 'Loading...' : 'Update Payment'}
                        </button>
                      )}
                    </div>
                    )}
                    {(!hasNaplexDlc || !hasPtcbDlc || !hasMpjeDlc) && (
                      <div className="card-actions">
                        {!hasNaplexDlc && (
                          <button
                            onClick={() => openPricingModal('naplex_crunch')}
                            className="settings-btn settings-btn-secondary"
                          >
                            Get NAPLEX DLC
                          </button>
                        )}
                        {!hasPtcbDlc && (
                          <button
                            onClick={() => openPricingModal('ptcb_crunch')}
                            className="settings-btn settings-btn-secondary"
                          >
                            Get PTCB DLC
                          </button>
                        )}
                        {!hasMpjeDlc && (
                          <button
                            onClick={() => openPricingModal('mpje_crunch')}
                            className="settings-btn settings-btn-secondary"
                          >
                            Get MPJE DLC
                          </button>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="plan-overview">
                    <div className="plan-header-row">
                      <div className="plan-name-group">
                        <span className="plan-tier-badge plan-tier-free">Free</span>
                      </div>
                    </div>
                    <p className="plan-upgrade-hint">
                      {ownedAddOns.length > 0
                        ? `Base account active with ${ownedAddOns.join(' + ')}.`
                        : 'Unlock unlimited uploads, quiz generation, and premium features.'}
                    </p>
                    {ownedAddOns.length > 0 && (
                      <div className="plan-meta">
                        <span className="plan-meta-label">Add-ons</span>
                        <span className="plan-meta-value">{ownedAddOns.join(', ')}</span>
                      </div>
                    )}
                    <div className="card-actions">
                      <button
                        onClick={() => openPricingModal()}
                        className="settings-btn settings-btn-upgrade"
                      >
                        Upgrade to Premium
                      </button>
                      {!hasNaplexDlc && (
                        <button
                          onClick={() => openPricingModal('naplex_crunch')}
                          className="settings-btn settings-btn-secondary"
                        >
                          Get NAPLEX DLC
                        </button>
                      )}
                      {!hasPtcbDlc && (
                        <button
                          onClick={() => openPricingModal('ptcb_crunch')}
                          className="settings-btn settings-btn-secondary"
                        >
                          Get PTCB DLC
                        </button>
                      )}
                      {!hasMpjeDlc && (
                        <button
                          onClick={() => openPricingModal('mpje_crunch')}
                          className="settings-btn settings-btn-secondary"
                        >
                          Get MPJE DLC
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Usage Section */}
            <section className="settings-section">
              <div className="section-label">
                <span className="section-label-text">Usage</span>
                <div className="section-label-line" />
              </div>
              <div className="settings-card">
                <UsageDashboard user={user} />
              </div>
            </section>

            {/* Account Section */}
            <section className="settings-section">
              <div className="section-label">
                <span className="section-label-text">Account</span>
                <div className="section-label-line" />
              </div>
              <div className="settings-card">
                <div className="account-grid">
                  <div className="account-field">
                    <span className="account-field-label">Email</span>
                    <span className="account-field-value">{user?.email}</span>
                  </div>
                  <div className="account-field">
                    <span className="account-field-label">Username</span>
                    <span className="account-field-value">{user?.username}</span>
                  </div>
                  <div className="account-field">
                    <span className="account-field-label">Role</span>
                    <span className="account-field-value" style={user?.role === 'admin' ? { color: '#fbbf24' } : undefined}>
                      {user?.role === 'admin' ? 'Admin' : user?.role === 'premium' ? 'Premium' : 'Base'}
                    </span>
                  </div>
                  <div className="account-field referral-field">
                    <span className="account-field-label">Code</span>
                    <div className="referral-input-group">
                      <input
                        type="text"
                        value={codeInput}
                        onChange={(e) => setCodeInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleApplyCode()}
                        placeholder="Enter code"
                        className="referral-input"
                        disabled={codeLoading}
                      />
                      <button
                        onClick={handleApplyCode}
                        disabled={codeLoading || !codeInput.trim()}
                        className="settings-btn settings-btn-secondary referral-btn"
                      >
                        {codeLoading ? '...' : 'Apply'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Tutorial */}
            <TutorialTestCard />

            {/* Study Class Section */}
            <section className="settings-section">
              <div className="section-label">
                <span className="section-label-text">Study Class</span>
                <div className="section-label-line" />
              </div>

              <div className="settings-card">
                {classLoading ? (
                  <p className="plan-upgrade-hint">Loading...</p>
                ) : myClasses.length === 0 ? (
                  <div className="plan-overview">
                    <p className="plan-upgrade-hint">
                      Join a study class to see classmate leaderboards and share decks.
                    </p>
                    <div className="account-field referral-field" style={{ borderTop: 'none', paddingTop: 0 }}>
                      <span className="account-field-label">Join Code</span>
                      <div className="referral-input-group">
                        <input
                          type="text"
                          value={joinCodeInput}
                          onChange={(e) => setJoinCodeInput(e.target.value.toUpperCase())}
                          onKeyDown={(e) => e.key === 'Enter' && handleJoinClass()}
                          placeholder="e.g. ABC123"
                          className="referral-input"
                          disabled={joinLoading}
                          maxLength={6}
                          style={{ textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 600 }}
                        />
                        <button
                          onClick={handleJoinClass}
                          disabled={joinLoading || !joinCodeInput.trim()}
                          className="settings-btn settings-btn-secondary referral-btn"
                        >
                          {joinLoading ? '...' : 'Join'}
                        </button>
                      </div>
                    </div>
                    <div className="card-actions" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                      {!showCreateForm ? (
                        <button
                          className="settings-btn settings-btn-secondary"
                          onClick={() => setShowCreateForm(true)}
                        >
                          Create a Class
                        </button>
                      ) : (
                        <div className="referral-input-group" style={{ width: '100%' }}>
                          <input
                            type="text"
                            value={createName}
                            onChange={(e) => setCreateName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleCreateClass()}
                            placeholder="Class name"
                            className="referral-input"
                            disabled={createLoading}
                            autoFocus
                          />
                          <button
                            onClick={handleCreateClass}
                            disabled={createLoading || !createName.trim()}
                            className="settings-btn settings-btn-primary referral-btn"
                          >
                            {createLoading ? '...' : 'Create'}
                          </button>
                          <button
                            onClick={() => { setShowCreateForm(false); setCreateName(''); }}
                            className="settings-btn settings-btn-secondary referral-btn"
                            style={{ padding: '0.45rem 0.6rem' }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="plan-overview">
                    {myClasses.map((cls) => (
                      <div key={cls.id} className="class-card-item">
                        <div className="plan-header-row">
                          <div className="plan-name-group">
                            <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{cls.name}</span>
                            <span style={{
                              fontSize: '0.7rem',
                              color: 'var(--text-subtle)',
                              background: 'var(--bg-surface)',
                              padding: '2px 8px',
                              borderRadius: 'var(--radius-full)',
                            }}>
                              {cls.member_count} member{cls.member_count !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                        <div className="plan-meta">
                          <span className="plan-meta-label">Join Code</span>
                          <button
                            onClick={() => handleCopyCode(cls.join_code)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.4rem',
                              padding: 0,
                              color: copiedCode === cls.join_code ? 'var(--color-success)' : 'var(--text-secondary)',
                              fontFamily: 'var(--font-mono, monospace)',
                              fontWeight: 700,
                              fontSize: 'var(--text-base)',
                              letterSpacing: '0.15em',
                            }}
                          >
                            {cls.join_code}
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5 }}>
                              {copiedCode === cls.join_code ? (
                                <polyline points="20 6 9 17 4 12" />
                              ) : (
                                <>
                                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                </>
                              )}
                            </svg>
                          </button>
                        </div>
                        <div className="plan-meta" style={{ justifyContent: 'space-between' }}>
                          <div>
                            <span className="plan-meta-label">Show Name</span>
                            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-subtle)', marginLeft: '0.5rem' }}>
                              {cls._showName !== undefined
                                ? (cls._showName ? 'Visible' : 'Hidden')
                                : 'Visible'}
                            </span>
                          </div>
                          <button
                            onClick={() => handleToggleVisibility(cls.id, cls._showName !== undefined ? cls._showName : true)}
                            style={{
                              width: 42,
                              height: 24,
                              borderRadius: 12,
                              border: 'none',
                              cursor: 'pointer',
                              background: (cls._showName !== undefined ? cls._showName : true) ? 'var(--accent-primary)' : 'rgba(255,255,255,0.15)',
                              position: 'relative',
                              transition: 'background 0.2s',
                              flexShrink: 0,
                            }}
                          >
                            <span
                              style={{
                                display: 'block',
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                background: '#fff',
                                position: 'absolute',
                                top: 3,
                                left: (cls._showName !== undefined ? cls._showName : true) ? 21 : 3,
                                transition: 'left 0.2s',
                              }}
                            />
                          </button>
                        </div>
                        <div className="card-actions">
                          {leaveConfirm === cls.id ? (
                            <>
                              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', alignSelf: 'center' }}>Leave this class?</span>
                              <button
                                className="settings-btn settings-btn-danger referral-btn"
                                onClick={() => handleLeaveClass(cls.id)}
                              >
                                Confirm
                              </button>
                              <button
                                className="settings-btn settings-btn-secondary referral-btn"
                                onClick={() => setLeaveConfirm(null)}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              className="settings-btn settings-btn-secondary"
                              onClick={() => setLeaveConfirm(cls.id)}
                              style={{ fontSize: 'var(--text-sm)' }}
                            >
                              Leave Class
                            </button>
                          )}
                        </div>
                      </div>
                    ))}

                    <div style={{ paddingTop: 'var(--space-3)', borderTop: '1px solid var(--border-subtle)' }}>
                      <div className="account-field referral-field" style={{ borderTop: 'none', paddingTop: 0 }}>
                        <span className="account-field-label">Join Another Class</span>
                        <div className="referral-input-group">
                          <input
                            type="text"
                            value={joinCodeInput}
                            onChange={(e) => setJoinCodeInput(e.target.value.toUpperCase())}
                            onKeyDown={(e) => e.key === 'Enter' && handleJoinClass()}
                            placeholder="e.g. ABC123"
                            className="referral-input"
                            disabled={joinLoading}
                            maxLength={6}
                            style={{ textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 600 }}
                          />
                          <button
                            onClick={handleJoinClass}
                            disabled={joinLoading || !joinCodeInput.trim()}
                            className="settings-btn settings-btn-secondary referral-btn"
                          >
                            {joinLoading ? '...' : 'Join'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Admin Section */}
            {user?.role === 'admin' && (
              <section className="settings-section">
                <div className="section-label">
                  <span className="section-label-text" style={{ color: '#fbbf24' }}>Admin</span>
                  <div className="section-label-line" />
                </div>

                <div className="settings-card">
                  <div className="plan-overview">
                    <div className="plan-header-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>Unlock All Golden Cards</span>
                        <p className="plan-upgrade-hint" style={{ margin: '4px 0 0' }}>
                          Bypasses achievement and currency requirements.
                        </p>
                      </div>
                      <button
                        onClick={handleToggleUnlockAllGolden}
                        disabled={goldenToggleLoading}
                        style={{
                          width: 48,
                          height: 26,
                          borderRadius: 13,
                          border: 'none',
                          cursor: goldenToggleLoading ? 'not-allowed' : 'pointer',
                          background: unlockAllGolden ? '#fbbf24' : 'rgba(255,255,255,0.15)',
                          position: 'relative',
                          transition: 'background 0.2s',
                          flexShrink: 0,
                        }}
                      >
                        <span
                          style={{
                            display: 'block',
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            background: '#fff',
                            position: 'absolute',
                            top: 3,
                            left: unlockAllGolden ? 25 : 3,
                            transition: 'left 0.2s',
                          }}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="settings-card" style={{ marginTop: 12 }}>
                  <div className="plan-overview">
                    <div className="plan-header-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>Reset NAPLEX Diagnostic</span>
                        <p className="plan-upgrade-hint" style={{ margin: '4px 0 0' }}>
                          Clears gauntlet completions, combat sessions, and learning maps.
                        </p>
                        {naplexResetResult && (
                          <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#86efac' }}>
                            Cleared {naplexResetResult.gauntlet_flags_cleared} gauntlets, {naplexResetResult.combat_sessions_deleted} combats, {naplexResetResult.learning_decks_deleted} learning decks
                          </p>
                        )}
                      </div>
                      <button
                        onClick={async () => {
                          setNaplexResetLoading(true);
                          setNaplexResetResult(null);
                          try {
                            const res = await apiClient.post('/api/naplex/admin/reset-diagnostic');
                            setNaplexResetResult(res.data);
                          } catch (e) {
                            console.error('Failed to reset NAPLEX diagnostic', e);
                          } finally {
                            setNaplexResetLoading(false);
                          }
                        }}
                        disabled={naplexResetLoading}
                        style={{
                          padding: '6px 16px',
                          borderRadius: 8,
                          border: 'none',
                          background: 'rgba(239, 68, 68, 0.2)',
                          color: '#f87171',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          cursor: naplexResetLoading ? 'not-allowed' : 'pointer',
                          flexShrink: 0,
                        }}
                      >
                        {naplexResetLoading ? 'Resetting...' : 'Reset'}
                      </button>
                    </div>
                  </div>
                </div>

              </section>
            )}

            {/* Legal */}
            <div className="sm-legal">
              <h3>Legal</h3>
              <AIDisclaimer variant="inline" />
              <div style={{ marginTop: '0.5rem' }}>
                <a href="/terms" style={{ marginRight: '1rem' }}>Terms of Service</a>
                <a href="/privacy">Privacy Policy</a>
              </div>
            </div>

            {/* Delete Account */}
            <div className="settings-section" style={{ borderTop: '1px solid rgba(239,68,68,0.2)', marginTop: 32, paddingTop: 24 }}>
              <h3 style={{ color: '#ef4444', fontSize: 14, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Danger Zone</h3>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, marginBottom: 12 }}>
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
              <button
                className="settings-btn settings-btn-danger"
                onClick={() => setShowDeleteAccountModal(true)}
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteAccountModal && (
        <div className="settings-modal-overlay" onClick={() => !isLoading && setShowDeleteAccountModal(false)}>
          <div className="settings-modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#ef4444' }}>Delete your account?</h3>
            <p style={{ color: 'rgba(255,255,255,0.7)', margin: '12px 0', lineHeight: 1.5 }}>
              This will permanently delete your account, all decks, progress, combat history, and subscription. <strong>This cannot be undone.</strong>
            </p>
            <div className="settings-modal-actions">
              <button
                className="settings-btn"
                onClick={() => setShowDeleteAccountModal(false)}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                className="settings-btn settings-btn-danger"
                onClick={async () => {
                  setIsLoading(true);
                  try {
                    await apiClient.delete('/api/auth/me');
                    logout();
                    window.location.href = '/?deleted=1';
                  } catch (err) {
                    setToastMessage(err.response?.data?.detail || 'Failed to delete account');
                    setShowToast(true);
                    setIsLoading(false);
                    setShowDeleteAccountModal(false);
                  }
                }}
                disabled={isLoading}
              >
                {isLoading ? 'Deleting...' : 'Yes, Delete Everything'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sub-modals rendered as siblings for clean z-index stacking */}

      {showCancelModal && (
        <div className="cancel-modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div
            className="cancel-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-subscription-title"
            aria-describedby="cancel-subscription-copy"
          >
            <div className="cancel-modal-header">
              <h2 id="cancel-subscription-title">Cancel Subscription</h2>
              <button
                className="cancel-modal-close"
                onClick={() => setShowCancelModal(false)}
                disabled={isLoading}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="cancel-modal-body" id="cancel-subscription-copy">
              <p>
                Are you sure you want to cancel your Premium subscription?
              </p>
              <p className="cancel-modal-warning">
                You'll keep premium access until <strong>{renewalDate}</strong>, then your account
                will automatically switch to the Free plan.
              </p>
            </div>
            <div className="cancel-modal-actions">
              <button
                className="settings-btn settings-btn-secondary"
                onClick={() => setShowCancelModal(false)}
                disabled={isLoading}
              >
                Keep Subscription
              </button>
              <button
                className="settings-btn settings-btn-danger"
                onClick={handleCancelSubscription}
                disabled={isLoading}
              >
                {isLoading ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast
        message={toastMessage}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </>
  );
}
